// src/stats/domain/services/statsService.ts

import type { RankingsData } from "~/src/player/domain/Player";
import type { GroupedRanking, GroupedRankingData } from "~/src/stats/domain/models/Rankings";
import { useWebContext } from "~/app/composables/webContext";

interface StatsService {
  mergeRankingsByPlayerId: (
    mainRankingData: RankingsData[],
    otherRankingsData: RankingsData[][]
  ) => GroupedRanking[];
  sortStatsTable: (
    sortValue: string,
    allStatsRequired: string[],
    mainStatFallback: string,
    seasonId: number,
    dataType: string,
    sortOrder: string | number,
    orderBy?: string
  ) => Promise<GroupedRanking[] | RankingsData[] | undefined>;
}

const mergeRankingsByPlayerId = (
  mainRankingData: RankingsData[],
  otherRankingsData: RankingsData[][]
): GroupedRanking[] => {
  const returnData: GroupedRanking[] = [];

  for (const mainRanking of mainRankingData) {
    if (!!mainRanking.ranking && !!mainRanking.total) {
      const playerId = mainRanking.player.id;
      const allPlayerRankingData: (GroupedRankingData | undefined)[] = [
        filterGroupedRankingData(mainRanking),
      ];
      for (const otherRanking of otherRankingsData) {
        const playerRankingData = otherRanking.find(data => data.player.id === playerId);
        allPlayerRankingData.push(
          playerRankingData ? filterGroupedRankingData(playerRankingData) : undefined
        );
      }

      const playerData: GroupedRanking = {
        playerId,
        team: mainRanking.team,
        player: mainRanking.player,
        rankingData: allPlayerRankingData,
      };

      returnData.push(playerData);
    }
  }

  return returnData;
};

const sortStatsTable = async (
  sortValue: string,
  allStatsRequired: string[],
  mainStatFallback: string,
  seasonId: number,
  dataType: string,
  sortOrder: string | number,
  orderBy?: string
): Promise<GroupedRanking[] | RankingsData[] | undefined> => {
  const { webContextGenders } = useWebContext();
  let newMainStat: string;
  let newOtherStats: string;

  const sortOrderString = sortOrder === -1 ? "ASC" : "DESC";

  if (!sortValue || !seasonId) return [];

  if (sortValue && seasonId) {
    newMainStat = sortValue ? sortValue : mainStatFallback;
    newOtherStats = allStatsRequired.filter(s => s !== newMainStat).join() || "";
    const addSecondaryStats = newOtherStats ? `&secondaryCodes=${newOtherStats}` : "";
    const data = await $fetch(
      `/api/stats?season=${seasonId}&type=${dataType}&mainCode=${newMainStat}${addSecondaryStats}&orderBy=${orderBy ? orderBy : "ranking"}&sortOrder=${sortOrderString}`,
      { query: { ctxGenders: webContextGenders } }
    );
    if (data) {
      return data;
    } else {
      return undefined;
    }
  }
};

const statsService = (): StatsService => ({
  mergeRankingsByPlayerId,
  sortStatsTable,
});

export default statsService;

function filterGroupedRankingData(RankingsData: RankingsData): GroupedRankingData {
  return {
    id: RankingsData.id,
    playerId: RankingsData.player.id,
    parameterCode: RankingsData.parameterCode,
    total: RankingsData?.total || 0,
    totalAVG: RankingsData?.totalAVG || 0,
    kamaScore: RankingsData.kamaScore,
    matchesPlayed: RankingsData.matchesPlayed,
    minutesPlayed: RankingsData.minutesPlayed,
    reliability: RankingsData.reliability,
    rankingAVG: RankingsData.rankingAVG,
    ranking: RankingsData.ranking,
  };
}
